import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BannerDisplay from '../components/BannerDisplay';
import AuctionCard from '../components/AuctionCard';
import { FilterPanel, EMPTY_FILTERS } from '../components/FilterPanel';
import { horseMatches } from '../config/horseOptions';

const AuctionsPage = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const isCompletate = searchParams.get('stato') === 'completate';
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAuctions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auctions`);
      setAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuctions();
  }, [fetchAuctions]);

  let filteredAuctions = auctions;
  
  // Filter by page mode: active or completed
  if (isCompletate) {
    filteredAuctions = filteredAuctions.filter(a => a.status === 'ended');
  } else {
    filteredAuctions = filteredAuctions.filter(a => a.status === 'active' || a.status === 'scheduled');
  }
  
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filteredAuctions = filteredAuctions.filter(a =>
      a.title?.toLowerCase().includes(q) ||
      a.horse?.name?.toLowerCase().includes(q) ||
      a.horse?.breed?.toLowerCase().includes(q) ||
      a.horse?.gender?.toLowerCase().includes(q) ||
      a.horse?.color?.toLowerCase().includes(q) ||
      a.horse?.location?.toLowerCase().includes(q)
    );
  }
  const baseAuctions = filteredAuctions;
  filteredAuctions = baseAuctions.filter(a => horseMatches(a.horse, filters));
  const countFor = (key, value) => baseAuctions.filter(a => horseMatches(a.horse, { ...filters, [key]: [value] })).length;

  // Sort by end_time: soonest closing first
  filteredAuctions.sort((a, b) => new Date(a.end_time) - new Date(b.end_time));

  const toggleFilter = (key, value) => setFilters(f => ({
    ...f,
    [key]: f[key].includes(value) ? f[key].filter(v => v !== value) : [...f[key], value],
  }));

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchQuery('');
  };

  const activeFilterCount = Object.values(filters).reduce((n, v) => n + v.length, 0) + (searchQuery.trim() ? 1 : 0);
  const panelProps = { filters, onToggle: toggleFilter, onReset: resetFilters, activeCount: activeFilterCount, countFor };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <section className="bg-[#0a1628] py-8 md:py-12">
        <div className="container-custom px-4">
          <h1 className="font-display text-2xl md:text-4xl text-white mb-2" data-testid="auctions-title">
            {isCompletate ? 'Aste Completate' : `${filteredAuctions.length} cavalli in vendita`}
          </h1>
          <p className="text-white/60 text-sm md:text-base mb-4 md:mb-6">Scopri i cavalli disponibili nelle nostre aste</p>
          {/* Search Bar */}
          <div className="relative max-w-xl">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Cerca per nome, razza, colore, luogo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-10 py-3 bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#c9a227] focus:bg-white/15 transition-all rounded-lg text-sm md:text-base"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                data-testid="search-clear-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-6 md:py-8">
        <div className="container-custom px-4">
          {/* Filter Toggle Button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="mb-4 md:mb-6 flex items-center gap-2 text-[#0a1628] font-medium hover:text-[#c9a227] transition-colors"
            data-testid="filter-toggle-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {sidebarOpen ? 'Nascondi filtri' : 'Mostra filtri'}
            {activeFilterCount > 0 && (
              <span className="bg-[#c9a227] text-[#0a1628] text-xs font-bold px-2 py-0.5 rounded-full">{activeFilterCount}</span>
            )}
          </button>

          {/* Mobile: Filters as collapsible panel above cards */}
          {sidebarOpen && (
            <div className="block lg:hidden mb-6" data-testid="filter-sidebar-mobile">
              <FilterPanel {...panelProps} compact />
            </div>
          )}

          <div className={`flex ${sidebarOpen ? 'gap-8' : 'gap-0'}`}>
            {/* Desktop Sidebar - hidden on mobile */}
            <div className={`hidden lg:block ${sidebarOpen ? 'w-64 min-w-[256px]' : 'w-0 min-w-0 overflow-hidden'} transition-all duration-300`} data-testid="filter-sidebar">
              <FilterPanel {...panelProps} />
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <BannerDisplay position="header" />

              {filteredAuctions.length === 0 ? (
                <div className="text-center py-20" data-testid="no-auctions-message">
                  <p className="text-[#64748b] text-lg mb-4">Nessuna asta trovata</p>
                  {activeFilterCount > 0 && (
                    <button onClick={resetFilters} className="text-[#c9a227] font-medium hover:underline">
                      Rimuovi tutti i filtri
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredAuctions.map(auction => (
                    <AuctionCard key={auction.auction_id} auction={auction} onRefresh={fetchAuctions} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AuctionsPage;
